/**
 * The village's farmers and what the gate may buy from them (V1):
 *   GET /farmers?q&groupId&missing&page&limit · GET /farmers/:id · PATCH /farmers/:id
 *   GET /harvest-readiness?groupId&cropId&ready · POST /harvest-readiness/mark
 * Farmers are onboarded field users of the village (groups are villages), found by their farmer ID
 * (e.g. RJ-AMOD-U001). The gate buys only from cultivations the office marked ready for harvest.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getOne, getPage, toQuery, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, iso, num, obj, str, strOrNull } from './mappers'

export interface Farmer {
  id: string
  farmerCode: string | null
  firstName: string
  lastName: string
  name: string
  careOf: string | null
  contactNo: string | null
  groupId: string | null
  villageName: string | null
  /** Media id (console upload) or a URL (onboarding); null = no photo. */
  photoId: string | null
  bankStatus: string | null
  bankLast4: string | null
  /** His cultivations marked ready for harvest: what the gate can buy from him. */
  readyCultivations: number
  createdAt: string | null
}

export function mapFarmer(r: Record<string, unknown>): Farmer {
  return {
    id: str(r.id),
    farmerCode: strOrNull(r.farmerCode),
    firstName: str(r.firstName),
    lastName: str(r.lastName),
    name: str(r.name) || `${str(r.firstName)} ${str(r.lastName)}`.trim(),
    careOf: strOrNull(r.careOf),
    contactNo: strOrNull(r.contactNo),
    groupId: strOrNull(r.groupId),
    villageName: strOrNull(r.villageName),
    photoId: strOrNull(r.photoId),
    bankStatus: strOrNull(r.bankStatus),
    bankLast4: strOrNull(r.bankLast4),
    readyCultivations: num(r.readyCultivations),
    createdAt: iso(r.createdAt),
  }
}

/** Why the gate cannot buy from him or check him yet. */
export function missingRosterDetails(f: Pick<Farmer, 'readyCultivations' | 'photoId'>): string[] {
  const out: string[] = []
  if (!f.readyCultivations) out.push('a crop ready for harvest')
  if (!f.photoId) out.push('a photo')
  return out
}

export type FarmerMissing = 'READY' | 'PHOTO' | 'ANY'

export interface FarmerFilters extends PageParams {
  q?: string
  groupId?: string
  missing?: FarmerMissing | ''
}

export function useFarmers(filters: FarmerFilters) {
  return usePageQuery([...ck.farmers, 'roster', filters], () =>
    getPage('/farmers', filters, mapFarmer),
  )
}

export function useFarmer(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.farmers, 'roster', 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/farmers/${id}`, mapFarmer),
  })
}

export interface FarmerInput {
  careOf?: string
  contactNo?: string
  photoMediaId?: string
}

export function useUpdateFarmer(id: string) {
  return useConsoleMutation<FarmerInput, Farmer>({
    mutationFn: async (body) => mapFarmer(obj(await api.patch(`/farmers/${id}`, body))),
    invalidate: [ck.farmers],
    success: 'Saved',
    errorTitle: 'Not saved',
    onError: () => true,
  })
}

// ---------------- ready for harvest ----------------

export interface ReadinessRow {
  cultivationId: string
  stage: string
  ready: boolean
  startDate: string | null
  cropId: string
  cropName: string
  varietyName: string | null
  plotLabel: string | null
  area: string | null
  areaUnit: string | null
  farmId: string
  farmName: string | null
  farmCode: string | null
  villageName: string
  farmers: Array<{ id: string; name: string; farmerCode: string }>
}

export const READY_STAGE = 'HARVESTING'

export function mapReadiness(r: Record<string, unknown>): ReadinessRow {
  return {
    cultivationId: str(r.cultivationId),
    stage: str(r.stage),
    ready: str(r.stage) === READY_STAGE,
    startDate: strOrNull(r.startDate),
    cropId: str(r.cropId),
    cropName: str(r.cropName),
    varietyName: strOrNull(r.varietyName),
    plotLabel: strOrNull(r.plotLabel),
    area: strOrNull(r.area),
    areaUnit: strOrNull(r.areaUnit),
    farmId: str(r.farmId),
    farmName: strOrNull(r.farmName),
    farmCode: strOrNull(r.farmCode),
    villageName: str(r.villageName),
    farmers: arr(r.farmers).map((x) => ({
      id: str(x.id),
      name: str(x.name),
      farmerCode: str(x.farmerCode),
    })),
  }
}

export interface ReadinessFilters {
  groupId?: string
  cropId?: string
  ready?: 'YES' | 'NO' | ''
}

export function useReadiness(f: ReadinessFilters) {
  return useQuery({
    queryKey: [...ck.farmers, 'readiness', f],
    enabled: Boolean(f.groupId),
    queryFn: async () => {
      const body = obj(await api.get('/harvest-readiness', { query: toQuery(f) }))
      return {
        rows: arr(body.rows).map(mapReadiness),
        ready: num(body.ready),
        total: num(body.count),
      }
    },
  })
}

export function useMarkReady() {
  return useConsoleMutation<
    { cultivationIds: string[]; ready: boolean },
    { changed: number; ready: boolean }
  >({
    mutationFn: async (body) => {
      const r = obj(await api.post('/harvest-readiness/mark', body))
      return { changed: num(r.changed), ready: Boolean(r.ready) }
    },
    invalidate: [ck.farmers],
    success: (r) =>
      r.ready
        ? `${r.changed} marked ready for harvest: phones get it with their next roster download`
        : `${r.changed} no longer ready for harvest`,
    errorTitle: 'Not changed',
  })
}
